import { useState, useEffect } from "react";
import { ResultCard } from "./components/result-card";
import { fetchSalaryCheckData } from "../../utils/api";
import { SalaryData } from "../../types/SalaryData";
import { MarketData } from "../../types/MarketData";

const parseJson = (value: unknown): any => {
  if (typeof value === "string") {
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  }
  return value;
};

 const situations: Record<number, string> = {
    1: "Er min løn fair?",
    2: "Lønforhandling",
    3: "Nyuddannet",
    4: "Søger nyt job",
  };

  
const getSituationName = (value: unknown): string => {
 
  if (typeof value === "number") {
    return situations[value] ?? String(value);
  }

  if (typeof value === "string") {
    const numeric = Number(value);
    if (value.trim() !== "" && Number.isFinite(numeric)) {
      return situations[numeric] ?? value;
    }
    return value;
  }

  return "";
};


const parseSalary = (value: unknown): number => {
  if (typeof value === "number") return value;
  if (typeof value === "string") {
    const normalized = value.replace(/[^0-9.,\-]/g, "").replace(/,/g, ".");
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
};

const mapToSalaryData = (data: any): SalaryData => ({
  name: data.name ?? "",
  created: data.created ? new Date(data.created) : new Date(),
  jobTitle: data.jobTitle ?? "",
  salary: parseSalary(data.salary),
  experience: data.jobExperience != null ? parseSalary(data.jobExperience) : null,
  education: data.educationLevel ?? "",
  region: data.region ?? "",
  age: data.ageGroup ?? "",
  ageGroup: data.ageGroup ?? "",
  jobSector: data.jobSector ?? "",
  managementExperience: data.managementExperience != null ? parseSalary(data.managementExperience) : null,
  situation: getSituationName(data.situation ?? data.situationId),
  situationId: data.situationId != null ? Number(data.situationId) : undefined,
  sector: data.jobSector ?? "",
  responsibility: data.responsibilityLevel != null ? String(data.responsibilityLevel) : "",
  purpose: "",
  jobTitleId: data.jobTitleId != null ? Number(data.jobTitleId) : 0,
});

const parseRangeValue = (label: string): number => {
  const match = label.match(/(\d+\.?\d*)/g);
  if (!match) return 0;
  const last = match[match.length - 1];
  const value = Number(last.replace(/\./g, ""));
  return Number.isFinite(value) ? value * (label.toLowerCase().includes("k") ? 1000 : 1) : 0;
};

const mapToMarketData = (data: any): MarketData | null => {
  const calc = parseJson(data.calculatedResultJson) ?? {};
  const average = parseSalary(calc.salaryAvg ?? data.salaryAvg ?? 0);
  const sampleSize = parseSalary(calc.numberOfSamples ?? data.numberOfSamples ?? 0);

  let rangeMin = 0;
  let rangeMax = 0;

  if (Array.isArray(calc.graphResult) && calc.graphResult.length > 0) {
    const firstLabel = String(calc.graphResult[0].xAxis ?? "");
    const lastLabel = String(calc.graphResult[calc.graphResult.length - 1].xAxis ?? "");
    rangeMin = parseRangeValue(firstLabel);
    rangeMax = parseRangeValue(lastLabel);
  }

  if (rangeMin === 0 && rangeMax === 0 && average > 0) {
    rangeMin = Math.round(average * 0.8);
    rangeMax = Math.round(average * 1.2);
  }

  const graphResult = Array.isArray(calc.graphResult)
    ? calc.graphResult.map((item: any) => ({
        xAxis: String(item.xAxis ?? ""),
        yAxis: parseSalary(item.yAxis ?? item.y ?? 0),
      }))
    : [];

  if (average > 0 && sampleSize > 0) {
    return { average, rangeMin, rangeMax, sampleSize, graphResult };
  }

  return null;
};

export default function SalaryCheckResult() {
  // 181f7c2c-78c2-4616-a4d6-1fcff71e6fe3
  const [infoKey, setInfoKey] = useState<string | null>(null);

  const [salaryData, setSalaryData] = useState<SalaryData>({
    name: "",
    created: new Date(),
    jobTitle: "",
    salary: 0,
    experience: null,
    education: "",
    region: "",
    age: "",
    situation: "",
    sector: "",
    responsibility: "",
    purpose: "",
    ageGroup: "",
    jobSector: "",
    managementExperience: null,
    jobTitleId: 0,
  });

  const [marketData, setMarketData] = useState<MarketData | null>(null);
  const [isCalculating, setIsCalculating] = useState(false);

  useEffect(() => {
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const storedInfoKey = urlParams.get("infoKey");
    if (storedInfoKey) {
      setInfoKey(storedInfoKey);
    }
    if (infoKey) {
      const loadSalaryData = async () => {
        setIsCalculating(true);

        try {
          const response = await fetchSalaryCheckData(infoKey);
          const data = response.data;

          setSalaryData(mapToSalaryData(data));
          setMarketData(mapToMarketData(data));
        } catch (error) {
          console.error("Failed to load salary check data", error);
        } finally {
          setIsCalculating(false);
        }
      };

      loadSalaryData();
    }
  }, [infoKey]);

  return (
    <>
      <div>
        <ResultCard salaryData={salaryData} marketData={marketData} isCalculating={isCalculating} />
      </div>
    </>
  );
}
